import { useTranslation } from 'react-i18next'

import { client } from '@/shared/api/client'
import { clarify } from '@/shared/components/ui/clarify'
import { OptimizationAction, OptimizationStatus } from '@/shared/generated'

import { useOptimizationQuery } from '../api/queries'
import { useBeforeGroupChange } from './useBeforeGroupChange'

type UseCheckOptimizationForDeleteExport = (
	fn: (...arr: any[]) => void
) => (...arg: any[]) => Promise<void>

export function useCheckOptimizationForDeleteExport(
	optimizationGroupId?: string | null
): UseCheckOptimizationForDeleteExport {
	const { t } = useTranslation()

	const { onMutate } = useBeforeGroupChange(optimizationGroupId)
	const { data: optimization } = useOptimizationQuery(optimizationGroupId!, {
		enabled: !!optimizationGroupId,
	})

	return (fn) =>
		async (...arg) => {
			if (
				optimizationGroupId &&
				optimization?.status === OptimizationStatus.Locked &&
				optimization?.is_exported
			) {
				const answer = await clarify({
					title: t('opt.confirm.revert_applied.title'),
					description: t('opt.confirm.revert_applied.description'),
					actions: [
						{
							actionId: 'keep_optimized_prices',
							ActionProps: { variant: 'secondary' },
							actionText: t(
								'opt.confirm.revert_applied.action.keep_optimized_prices'
							),
						},
						{
							actionId: 'revert_full_state',
							ActionProps: { variant: 'primary' },
							actionText: t(
								'opt.confirm.revert_applied.action.revert_full_state'
							),
						},
					],
				})
				if (!answer || answer === 'close') {
					return
				}

				if (answer === 'revert_full_state') {
					await client.optimizations.optimizationAction({
						optimizationGroupId,
						action: OptimizationAction.DeleteExport,
					})
				}

				return await fn(...arg)
			} else if (optimization?.status === OptimizationStatus.Finished) {
				await onMutate()

				return await fn(...arg)
			} else {
				return await fn(...arg)
			}
		}
}
